import { Pill, SearchControl } from 'admination-react'
import { useLayoutEffect, useRef } from 'react'
import { FilterMenu, type FilterMenuRow } from '@/core/components/dashboard/FilterMenu'
import { SortControl, type SortState } from '@/core/components/dashboard/SortControl'
import { ViewTypeMenu } from './ViewTypeMenu'
import { supportsBoardView, type ViewPipelineConfig } from './viewConfig'
import type { ViewType } from '@/core/lib/viewTypePrefs'

export function ListToolbar<T>({
  config,
  viewType,
  onViewTypeChange,
  filterRows,
  filterActive,
  sort,
  onSortChange,
  search,
  onSearchChange,
  className,
}: {
  className?: string
  config: ViewPipelineConfig<T>
  viewType?: ViewType
  onViewTypeChange?: (v: ViewType) => void
  filterRows: FilterMenuRow[]
  filterActive: boolean
  sort: SortState
  onSortChange: (sort: NonNullable<SortState>) => void
  search: string
  onSearchChange: (query: string) => void
}) {
  const showViewType = supportsBoardView(config) && viewType !== undefined && !!onViewTypeChange
  const showFilter = filterRows.length > 0
  const pillRef = useRef<HTMLDivElement>(null)
  const positions = useRef(new Map<HTMLElement, { x: number; y: number }>())
  const animations = useRef(new Map<HTMLElement, Animation>())

  useLayoutEffect(() => {
    const pill = pillRef.current
    if (!pill) return
    const bounds = pill.getBoundingClientRect()
    const localCenter = (element: HTMLElement) => {
      const rect = element.getBoundingClientRect()
      return { x: rect.x + rect.width / 2 - bounds.x, y: rect.y + rect.height / 2 - bounds.y }
    }
    const motion = getComputedStyle(pill)
    const options = {
      duration: parseFloat(motion.getPropertyValue('--dur-base')) || 250,
      easing: motion.getPropertyValue('--ease-out').trim() || 'ease-out',
    }
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    const next = new Map<HTMLElement, { x: number; y: number }>()
    for (const element of Array.from(pill.children) as HTMLElement[]) {

      if (element.classList.contains('pill-menu--retained')) continue
      const displayed = localCenter(element)
      animations.current.get(element)?.cancel()
      animations.current.delete(element)
      const target = localCenter(element)
      next.set(element, target)
      const previous = positions.current.get(element)
      if (reduced || !element.animate) continue
      if (previous && (previous.x !== target.x || previous.y !== target.y)) {

        const x = previous.x - target.x + (displayed.x - target.x)
        const y = previous.y - target.y + (displayed.y - target.y)
        animations.current.set(element, element.animate([
          { transform: `translate(${x}px, ${y}px)` },
          { transform: 'translate(0, 0)' },
        ], options))
      } else if (!previous && positions.current.size) {
        animations.current.set(element, element.animate([{ opacity: 0 }, { opacity: 1 }], options))
      }
    }
    for (const [element, animation] of animations.current) {
      if (!next.has(element)) { animation.cancel(); animations.current.delete(element) }
    }
    positions.current = next
  }, [showViewType, showFilter])

  useLayoutEffect(() => () => {
    for (const animation of animations.current.values()) animation.cancel()
    animations.current.clear()
  }, [])

  return (
    <Pill ref={pillRef} className={className ? `pill--tools ${className}` : 'pill--tools'}>
      {showViewType && <ViewTypeMenu value={viewType!} onChange={onViewTypeChange!} />}
      <SortControl sort={sort} onChange={onSortChange} options={config.sortKeys ?? []} />
      {showFilter && <FilterMenu rows={filterRows} active={filterActive} />}
      <SearchControl value={search} onChange={onSearchChange} placeholder="Suchen…" label="Suchen" />
    </Pill>
  )
}
